import type { FunctionComponent } from 'preact';

import type {
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { replaceValue } from 'Core/actions/request';
import { useDispatch } from 'Core/store';
import { FacetValue } from 'Models/index';
import { cloneSafe } from 'Utils/components';
import { getRangeFromTerm, type MinMax } from 'Utils/ranged';
import { simpleHandler } from 'Utils/roleHandler';

import type { CommonParams, FacetCommonProps } from '../baseFacet';
import { RangedInputs, SimpleValue } from '../common/index';
import type { Params as RangedInputsParams } from '../common/rangedInputs';
import type { SimpleValueTemplateParams } from '../common/simpleValue';

type Params = Omit<CommonParams, 'selection'> & {
  Inputs: TemplateFunctionInvoker<RangedInputsParams>;
  Values: RepeaterFunctionInvoker<SimpleValueTemplateParams>;
};

const RangedListFacet: FunctionComponent<FacetCommonProps<Params>> = ({
  facet: { facetedValues, selection },
  field,
  config: { step = '1' },
  templateFunc,
  facetRef,
  commonParams,
  commonRoles,
}) => {
  const dispatch = useDispatch();

  if (!facetedValues.length) {
    return null;
  }

  const selectedTerms: MinMax = selection.length === 1 ? getRangeFromTerm(selection[0].term) : ['*', '*'];

  function handleCustomRange([minValue, maxValue]: MinMax) {
    const term = FacetValue.createRangedTerm(minValue, maxValue);
    const isSelected = !(minValue === '*' && maxValue === '*');
    dispatch(replaceValue({ term, field, isUnique: true, isSelected }, { mayDiscardValue: true }));
  }

  const Inputs = (templ: TemplateFunction<RangedInputsParams>) =>
    (
      <RangedInputs
        template={templ}
        selectedValues={selectedTerms}
        handleCustomRange={handleCustomRange}
        key={`inputs-for-${field}`}
        step={step}
      />
    ) as TemplateResult;

  const Values = facetedValues.map((value) => (templ) => {
    const props = {
      value,
      template: templ,
    };
    return <SimpleValue {...props} key={FacetValue.termKey(value)} />;
  }) as RepeaterFunctionInvoker<SimpleValueTemplateParams>;

  const onClick = simpleHandler({ ...commonRoles });

  const component = templateFunc.call({ ...commonParams, Inputs, Values });
  return cloneSafe(component, facetRef, { onClick });
};

export default RangedListFacet;
